import { IXmlNode } from '@/app-modules/server-communications';
import { IPrizeStructure, IPrizeTicketBook, IPrizeStake, IPrizeElement } from '@/components/game/implementations/_default/_domain/models';
import { CatchUpData, ResetData, CallsData, NextGameData, PrizeData, getGameConfiguration } from '@/components/game/_domain';
import { Bingo60PrizeType, getPrizeTypeName } from '@/components/game/implementations/uk/bingo-60/_domain/models/prize-type';
import { Bingo60TicketBook, getTicketBookNameForTicketBookId, getTicketBookSequence } from '@/components/game/implementations/uk/bingo-60/_domain/models/ticket-book';
import { ConfigurationPaths } from '@/app-modules/config/models';

class Bingo60PrizeData extends PrizeData {
  constructor(node: IXmlNode) {
    super(node);
  }

  private get stakeValues(): number[] {
    return [25, 50, 100, 200];
  }

  private get jackpotCalls(): number[] {
    const gameConfiguration = getGameConfiguration();
    const gameConfigurationPaths = ConfigurationPaths.forGame('bingo60');
    const jackpotCalls = gameConfiguration.getConfigurationByPath(gameConfigurationPaths.jackpotcalls);
    return jackpotCalls.split(',').map((item) => parseInt(item, 10));
  }

  private get ticketBookSequence(): Bingo60TicketBook[] {
    return getTicketBookSequence();
  }

  private get prizeTypes(): Bingo60PrizeType[] {
    return [
      Bingo60PrizeType.Prize,
      Bingo60PrizeType.Jackpot
    ];
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    return {
      stakes: this.stakeValues.map((stakeValue) => this.getPrizeStakeForStakeValue(stakeValue, node))
    };
  }

  private getPrizeStakeForStakeValue(stakeValue: number, node: IXmlNode): IPrizeStake {
    return {
      stakeValue,
      ticketBooks: this.ticketBookSequence.map((ticketBookId) => this.getPrizeStructureForStakeValueAndTicketBook(stakeValue, ticketBookId, node))
    };
  }

  private getPrizeStructureForStakeValueAndTicketBook(stakeValue: number, ticketBookId: Bingo60TicketBook, node: IXmlNode): IPrizeTicketBook {
    const prizeValueCsv = this.getPrizeValueCsvForStakeValue(stakeValue, node);
    const jackpotValueCsv = this.getJackpotValueCsvForStakeValue(stakeValue);
    return {
      id: ticketBookId.toString(),
      name: getTicketBookNameForTicketBookId(ticketBookId),
      prizes: this.prizeTypes.map((prizeType) => {
        const prizeCsv = (prizeType === Bingo60PrizeType.Jackpot) ? jackpotValueCsv : prizeValueCsv;
        return this.getPrize(prizeType, prizeCsv, ticketBookId);
      })
    };
  }

  private getPrizeValueCsvForStakeValue(stakeValue: number, node: IXmlNode): string {
    const prizeAttributeName = `prize${stakeValue}`;
    return node.attributes[prizeAttributeName];
  }

  private getJackpotValueCsvForStakeValue(stakeValue: number): string {
    const jackPotBaseValues = [125, 250, 500, 1000, 2500]; // TODO (DS) Will be retrieved from config https://tombola.atlassian.net/browse/BP-1360
    const stakeIndex = this.stakeValues.indexOf(stakeValue);
    const stakeMultiplier = Math.pow(2, stakeIndex);
    return jackPotBaseValues.map((jackpotValue) => jackpotValue * stakeMultiplier * 100).join(',');
  }

  private getPrize(prizeType: Bingo60PrizeType, prizeCsv: string, ticketBookId: Bingo60TicketBook): IPrizeElement {
    const isJackpot = prizeType === Bingo60PrizeType.Jackpot;

    return {
      name: getPrizeTypeName(prizeType),
      prize: this.getPrizeValueForTicketBook(prizeCsv, ticketBookId),
      disablePrize: false,
      isJackpot,
      jackpotCalls: isJackpot ? this.getjackpotCallsForTicketBook(ticketBookId) : 0
    };
  }

  private getjackpotCallsForTicketBook(ticketBookId: Bingo60TicketBook): number {
    const ticketBookIndex = this.getTicketBookIndexForTicketBookId(ticketBookId);

    return Number(this.jackpotCalls[ticketBookIndex]);
  }

  private getPrizeValueForTicketBook(prizeCsv: string, ticketBookId: Bingo60TicketBook): number {
    if (prizeCsv) {
      const ticketBookIndex = this.getTicketBookIndexForTicketBookId(ticketBookId);
      const prizeValues = prizeCsv.split(',');
      if (ticketBookIndex >= 0 && ticketBookIndex < prizeValues.length) {
        return Number(prizeValues[ticketBookIndex]);
      }
    }
    return 0;
  }

  private getTicketBookIndexForTicketBookId(ticketBookId: Bingo60TicketBook): number {
    return this.ticketBookSequence.indexOf(ticketBookId);
  }
}

class Bingo60CatchUpData extends CatchUpData {
  private prizeData: Bingo60PrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new Bingo60PrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

class Bingo60ResetData extends ResetData {
  private prizeData: Bingo60PrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new Bingo60PrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

class Bingo60CallsData extends CallsData {
  constructor(node: IXmlNode) {
    super(node);
  }
}

class Bingo60NextGameData extends NextGameData {
  private prizeData: Bingo60PrizeData;

  constructor(node: IXmlNode) {
    super(node);
  }

  protected getPrizeStructure(node: IXmlNode): IPrizeStructure {
    if (!this.prizeData) {
      this.prizeData = new Bingo60PrizeData(node);
    }
    return this.prizeData.prizeStructure;
  }
}

export {
  Bingo60CatchUpData,
  Bingo60ResetData,
  Bingo60CallsData,
  Bingo60NextGameData
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/game/implementations/uk/bingo-60/_domain/models/prize.ts